import React from "react";
import axios from 'axios';
import moment from 'moment';
import {qnectFormValidator} from "../components/qnect-ui/qnect-validator";
import {v4 as uuid} from 'uuid';
import {uniqueId} from "lodash";


export const updateObject = (oldObject, updatedProperties) => {
    return {
        ...oldObject,
        ...updatedProperties
    };
};

export const checkValidity = (value, rules) => {
    let isValid = true;
    if (!rules) {
        return true;
    }

    if (rules.required) {
        isValid = value.trim() !== '' && isValid;
    }

    if (rules.minLength) {
        isValid = value.length >= rules.minLength && isValid
    }

    if (rules.maxLength) {
        isValid = value.length <= rules.maxLength && isValid
    }

    if (rules.isEmail) {
        const pattern = /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
        isValid = pattern.test(value) && isValid
    }

    if (rules.isNumeric) {
        const pattern = /^\d+$/;
        isValid = pattern.test(value) && isValid
    }

    return isValid;
}

export function _formatAmount(amount, locale, prefix, minFractionDigit = 0, maxFractionDigit = 2) {
    let value = _removeFormatting(amount);

    if (_isNotDecimalChar(value)) {
        return "";
    }

    let _prefix = prefix;

    return (_prefix || "") + Intl.NumberFormat(locale || "en", {
        maximumFractionDigits: maxFractionDigit,
        minimumFractionDigits: minFractionDigit
    }).format(value);
}

export function _valueOf(formattedAmount) {
    let value = _removeFormatting(formattedAmount);

    if (_isNotDecimalChar(value)) {
        return NaN;
    } else {
        return Number(value);
    }
}

export function _removeFormatting(value) {
    if (!!value) {
        return "".concat(value).replace(/[^0-9.]/g, "");
    } else {
        return "";
    }
}

export function _isNotDecimalChar(value) {
    if (!value || isNaN(value)) {
        return true;
    } else {
        return false;
    }
}

let scrollTimer = null;
export function _scrollToComponent(ref) {
    const isIE = /*@cc_on!@*/false || !!document.documentMode;

    // prevent multiple call to scroll.
    if(!scrollTimer){
        scrollTimer = setTimeout(function(){
            if(isIE){
                window.scrollTo(0, ref?.current?.offsetTop);
            } else {
                window.scrollTo({top: ref?.current?.offsetTop, behavior: 'smooth'});
            }
            scrollTimer = null;
        }, 500);
    }
}

export function _scrollTo(y) {

    // prevent multiple call to scroll.
    if(!scrollTimer){
        scrollTimer = setTimeout(function(){
            window.scrollTo(0, y);

            scrollTimer = null;
        }, 500);
    }
}


export function _scrollToTop() {
    const isIE = /*@cc_on!@*/false || !!document.documentMode;

    // prevent multiple call to scroll.
    if(!scrollTimer){
        scrollTimer = setTimeout(function(){
            if(isIE){
                window.scrollTo(0, 0);
            } else {
                window.scrollTo({top: 0, behavior: 'smooth'});
            }
            scrollTimer = null;
        }, 500);
    }
}


export async function _sendLog(message, type) {
    return new Promise(function (resolve, reject) {
        if (process.env.REACT_APP_SEND_LOG && message) {
            let data = ''
            if (Array.isArray(message)) {
                data = message.join(', ')
            } else {
                data = message;
            }
            axios.post(process.env.REACT_APP_SEND_LOG + (!type ? 'error' : type), {'data': data},
                {headers: {}, shouldIntercept: 'false'})
                .then(resp => {
                    resolve(resp.data);
                })
                .catch(error => {
                    resolve();
                });
        }
    })
}

export const onChangeCardNumberFormatHandler = (event, setCardNumberMethod) => {
    let cardNoRegex = new RegExp("^\\d{4}-\\d{4}-\\d{4}-\\d{4}$", "g");
    let newCardNo = '';
    let preventDefault = false;
    if (!cardNoRegex.test(event.target.value)) {
        let cardNo = event.target.value.replace(/-/g, '');
        if (cardNo.length > 16) {
            cardNo = cardNo.substring(0, 16);
        }

        if (!(/\D/g.test(cardNo))) {
            do {
                if (cardNo.length > 4) {
                    newCardNo = newCardNo + cardNo.substring(0, 4) + '-';
                    cardNo = cardNo.substring(4, cardNo.length);
                } else {
                    newCardNo = newCardNo + cardNo;
                    cardNo = '';
                }
            } while (cardNo.length > 0);
        } else {
            preventDefault = true;
        }
    } else {
        newCardNo = event.target.value;
    }

    if (!preventDefault) {
        setCardNumberMethod(newCardNo);
    } else {
        event.preventDefault();
    }
};

export const onChangeExpiryDateHandler = (event, setExpiryDateMethod) => {
    let newExpiryDate = event.target.value.trim();
    if (newExpiryDate) {
        if (newExpiryDate.length === 4 && newExpiryDate.indexOf("-") === -1) {
            newExpiryDate = newExpiryDate.substring(0, 2) + '-' + newExpiryDate.substring(2);
        }
    }

    setExpiryDateMethod(newExpiryDate);
};


export const _validateForm = (formId) => {
    let valid = true;

    if (qnectFormValidator.checkForm(formId).isValid !== true) {
        valid = false;
    }

    return valid;
}

export const _refreshFormField = (formId, field) => {
    qnectFormValidator.refreshItem(formId, field);
}

export const getDocName = (doc, cfg, country)  => {
    if (doc) {
        country = country === 'HGI' ? 'HKG' : country;
        let documentType = doc?.dDocType.replace('ASIA-', '');
        let documentName = cfg('policyDocumentNaming.' + country + '.' + documentType);

        if (!documentName.startsWith('policyDocumentNaming.')) {
            // affix (CHINESE) on document name display if document title found has (CHINESE)
            if (doc.documentTitle && doc.documentTitle.includes("(CHINESE)")) {
                documentName = documentName + " (CHINESE)";
            }
            return documentName;
        } else {
            if (doc?.documentTitle) {
                return doc.documentTitle.replace(' - Client Copy', '')
            }
            if (doc?.did === 0) {
                return doc.ddocTitle;
            }
            return doc?.dDocType.replace('ASIA-', '');
        }
    }
    return '';
}

export const customReplaceAll = (regex, stringToBeChanged, changeToThisCharacter) => {
    let matchedString = stringToBeChanged.match(regex);
    if (matchedString[0]) {
        let changedString = matchedString[0].replaceAll(/./g, changeToThisCharacter);
        return stringToBeChanged.replaceAll(regex, changedString);
    }
}

export const _isRenewalStatus = (statusCode) => {
    if (statusCode === 'AR' || statusCode === 'FR' || statusCode === 'SR' || statusCode === 'PP') {
        return true;
    }

    return false;
}

export const _getPolPaymentCurrency = (country) => {
    switch (country) {
        case "HKG":
            return "HKD";
        case "SGP":
            return "SGD";
        case "MYS":
            return "MYR";
        default:
            return "N/A"
    }
}

export const _formatDate = (date, format = 'DD/MM/YYYY') => {
    let dateString = moment(date).format(format);
    return dateString === 'Invalid date' ? '' : dateString;
}

/** this will set time to UTC+8
 *  used for viewing only */
export const _formatDateTimeZone = (date, format = 'DD/MM/YYYY') => {
    if (date) {
        let javascriptDate = new Date(date);
        let diffMinutes = 480 + javascriptDate.getTimezoneOffset();
        let dateString = moment(date).add(diffMinutes,'minutes').format(format);
        return dateString === 'Invalid date' ? '' : dateString;
    } else {
        return '';
    }
}

export const _masked = (val) => {
    if (val) {
        let mask_char = '•';
        let mask_no = 4;
        if (val.length <= mask_no) {
            return val;
        }
        else {
            return ('' + val).slice(0, 0 - mask_no).replace(/./g, mask_char)
                + ('' + val).slice(0 - mask_no);
        }
    }
    else {
        return val;
    }
}

export const _validateNric = (nricString) => {
    if (!nricString || nricString === '') {
        return false;
    }

    if (nricString.length !== 9) {
        return false;
    }

    let total;
    let numericNric;
    const first = nricString[0];
    const last = nricString[nricString.length - 1];

    if ("STFGM".indexOf(first) < 0) {
        return false;
    }

    numericNric = nricString.substr(1, nricString.length - 2);

    if (isNaN(numericNric)) {
        return false
    }

    total = modNric(numericNric);
    let offset = (first === "T" || first === "G") ? 4 : (first === "M") ? 3 : 0;
    let index = (offset + total) % 11;

    if (first === "M") {
        index = 10 - index;
    }

    let outputs;

    if (first === 'S' || first === 'T') {
        outputs = [ 'J', 'Z', 'I', 'H', 'G', 'F', 'E', 'D', 'C', 'B', 'A' ];
    } else if (first === 'F' || first === 'G') {
        outputs = [ 'X', 'W', 'U', 'T', 'R', 'Q', 'P', 'N', 'M', 'L', 'K' ];
    } else if (first === 'M') {
        outputs = [ 'K', 'L', 'J', 'N', 'P', 'Q', 'R', 'T', 'U', 'W', 'X' ];
    }

    return last === outputs[index];
}

function modNric(numericNric) {
    const multiples = [ 2, 7, 6, 5, 4, 3, 2 ];

    let count = 0, total = 0;
    while (numericNric !== 0) {
        total += (numericNric % 10) * multiples[multiples.length - (1 + count++)];
        numericNric /= 10;
        numericNric = Math.floor(numericNric);
    }
    return total;
}

export const _generateUuid = () => {
    return uuid();
}

export const _convertStringDateToMomentObject = (dateString, format = 'DD/MM/YYYY') => {
    if (typeof dateString === 'string' || dateString instanceof String){
        return moment(dateString, format);
    }
    return dateString;
}

export const isParentMember = (memberNo) => {
    return memberNo && memberNo.endsWith ('-00');
}

export const getParentMemberOptions = (members) => {
    // populate parent member list
    return members.filter(member => member.relationship === '00')
        .map(m => ({
            optionValue: m.memberNo,
            optionLabel: m.memberNo + ' ' + m.name
        }));
}

export const generateMemberId = (val) => {
    let id = ('0000'+val).slice(-4);
    return '0' + id + '-00';
}

export const getRelationshipLabel = (relationship) => {
    switch(relationship) {
        case '00' :
            return 'Self';
            break;
        case '01' :
            return 'Spouse';
            break;
        case '02' :
            return 'Child';
            break;
    }
}

export const getGenderLabel = (genderCode) => {
    switch(genderCode) {
        case 'M' :
            return 'Male';
            break;
        case 'F' :
            return 'Female';
            break;
        default:
            return '';
            break;
    }
}
